import React, { useState } from "react";
import { Resources, UseOfFundsValue } from "../../utils/resources";
import { useForm } from "../../contexts/FormContext";
import alertIcon from "../../assets/icons/alert-icon.svg";

const DEFAULT_LOAN_AMOUNT = 500;

const Step1: React.FC = () => {
  const {
    formData,
    updateField,
    validateField,
    isFieldTouched,
    touchedFields,
  } = useForm();
  const [loanAmount, setLoanAmount] = useState<number>(
    Number(formData.borrow_amount) || DEFAULT_LOAN_AMOUNT,
  );

  const handleAmountChange = (value: number) => {
    setLoanAmount(value);
    updateField("borrow_amount", value.toString());
  };

  const handleUseOfFundsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as UseOfFundsValue;
    updateField("use_of_funds", value);
  };

  // Show errors if field is touched or if form submission was attempted
  const amountError =
    isFieldTouched("borrow_amount") || touchedFields["borrow_amount"]
      ? validateField("borrow_amount", loanAmount.toString())
      : null;

  const useOfFundsError =
    isFieldTouched("use_of_funds") || touchedFields["use_of_funds"]
      ? validateField("use_of_funds", formData.use_of_funds || "")
      : null;

  return (
    <div className="flex flex-col space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Let us help you find a quote</h1>
        <p className="mt-2">No obligation, quick and simple process</p>
      </div>

      {/* Alert Box */}
      <div className="bg-grey-60 rounded-20 p-4 flex items-center space-x-3">
        <img alt="alert icon" src={alertIcon} />
        <p>Getting a quote from a lender won't affect your credit score</p>
      </div>

      {/* Loan Amount Input */}
      <div className="space-y-4">
        <label className="block text-sm font-medium">
          How much do you need to borrow?
        </label>
        <div className="relative mt-1">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center">
            £
          </span>
          <input
            type="number"
            name="loan_amount"
            value={loanAmount}
            onChange={(e) => handleAmountChange(Number(e.target.value))}
            className={`form-input-secondary ${
              amountError ? "border-red" : "border-gray-20"
            } pl-6`}
            min={100}
            max={5000}
            aria-invalid={!!amountError}
            aria-describedby={amountError ? "amount-error" : undefined}
          />
        </div>
        {amountError && (
          <p className="mt-2 text-sm text-red" id="amount-error" role="alert">
            {amountError}
          </p>
        )}

        {/* Slider */}
        <div className="space-y-2">
          <input
            type="range"
            name="loan_amount_slider"
            min={100}
            max={5000}
            step={10}
            value={loanAmount}
            onChange={(e) => handleAmountChange(Number(e.target.value))}
            className="loan-range-slider"
          />
          <div className="flex justify-between text-sm">
            <span>£100</span>
            <span>£5000</span>
          </div>
        </div>
      </div>

      {/* Use of Funds Dropdown */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">
          What will you use this money for?
        </label>
        <select
          value={formData.use_of_funds || ""}
          name="use_of_funds"
          onChange={handleUseOfFundsChange}
          className={`form-input-secondary ${
            useOfFundsError ? "border-red" : ""
          }`}
          aria-invalid={!!useOfFundsError}
          aria-describedby={useOfFundsError ? "use-of-funds-error" : undefined}
        >
          <option value="">Select a purpose</option>
          {Object.entries(Resources.useOfFunds).map(([label, value]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {useOfFundsError && (
          <p
            className="mt-2 text-sm text-red"
            id="use-of-funds-error"
            role="alert"
          >
            {useOfFundsError}
          </p>
        )}
      </div>
    </div>
  );
};

export default Step1;
